import { Link } from "react-router-dom";
import { PHONE_HREF, PHONE, YEARS_EXPERIENCE, BADGE_URL, GALLERY_IMAGES } from "../../mocks/businessData";
import TrustBar from "../../components/feature/TrustBar";
import AIAnswerBlock from "../../components/feature/AIAnswerBlock";
import ProcessSection from "../../components/feature/ProcessSection";
import WhyChooseUs from "../../components/feature/WhyChooseUs";
import ServiceAreasSection from "../../components/feature/ServiceAreasSection";
import Testimonials from "../../components/feature/Testimonials";
import FAQSection from "../../components/feature/FAQSection";
import FinalCTA from "../../components/feature/FinalCTA";

const aboutFAQs = [
  {
    q: "How long has Upper Canada Landscape been in business?",
    a: `Upper Canada Landscape has been serving London, Ontario and surrounding communities for over ${YEARS_EXPERIENCE} years. Founded with a commitment to artisan craftsmanship and client satisfaction, we have grown to become one of Southwestern Ontario's most trusted landscaping companies.`,
  },
  {
    q: "Who is Upper Canada Landscape?",
    a: "Upper Canada Landscape is a full-service landscape design and construction company based in London, Ontario. We specialize in custom outdoor spaces including hardscape, water features, synthetic turf, and complete landscape design for residential properties throughout Southwestern Ontario.",
  },
  {
    q: "Does Upper Canada Landscape use subcontractors?",
    a: "No. All work is performed by our own trained, in-house crews. We do not subcontract our installations. This ensures consistent quality, accountability, and craftsmanship standards on every project we take on.",
  },
  {
    q: "Is Upper Canada Landscape licensed and insured in Ontario?",
    a: "Yes. We are fully licensed and carry comprehensive liability insurance for all work performed in Ontario. Our crews are trained professionals and our business practices meet all provincial requirements.",
  },
  {
    q: "What communities does Upper Canada Landscape serve?",
    a: "We serve London, St. Thomas, Sarnia, Kitchener, Stratford, Grand Bend, and communities throughout Middlesex, Elgin, Oxford, and Huron Counties in Southwestern Ontario.",
  },
];

const aboutAIContent = {
  heading: "About Upper Canada Landscape — London, Ontario",
  paragraphs: [
    {
      lead: "Who we are:",
      body: "Upper Canada Landscape is a premier outdoor living company based in London, Ontario, with 30 years of experience transforming residential properties into exceptional outdoor environments. We are a family-focused business built on artisan craftsmanship, honest communication, and long-term client relationships.",
    },
    {
      lead: "Our founding story:",
      body: "Established to bring a higher standard of quality to London area landscaping, Upper Canada Landscape has grown from a small crew of dedicated craftspeople into a full-service landscape design and construction company serving communities throughout Southwestern Ontario.",
    },
    {
      lead: "Our philosophy:",
      body: "We believe every outdoor space has the potential to become something extraordinary. We work closely with each client to understand how they live, what they love, and how their property can be transformed to reflect that vision \u2014 practically, beautifully, and durably.",
    },
    {
      lead: "Our commitment:",
      body: "Every project is executed with artisan attention to detail, premium materials, and in-house crews who take personal pride in their work. We don\u2019t consider a project complete until the client is completely satisfied.",
    },
  ],
};

export default function AboutPage() {
  return (
    <main>
      {/* 1. Hero */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://uppercanadalandscape.com/wp-content/uploads/2025/10/landscaping-design-07.webp"
            alt="Upper Canada Landscape – premium landscape design in London, Ontario"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-16 pb-16 pt-36">
          <div className="flex items-center gap-4 mb-5">
            <span className="h-px w-10 bg-rust-400 block flex-shrink-0"></span>
            <span className="font-sans text-xs font-semibold tracking-[0.26em] uppercase text-rust-300">
              Our Story
            </span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white leading-tight mb-5 max-w-3xl">
            {YEARS_EXPERIENCE} Years of Building<br />
            Exceptional Outdoor Spaces
          </h1>
          <p className="font-sans text-lg text-stone-300 max-w-xl mb-9">
            Upper Canada Landscape was founded on a simple belief: every property deserves to be
            transformed into something extraordinary.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/#contact"
              onClick={() => {
                setTimeout(() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
              className="whitespace-nowrap inline-flex items-center gap-2 bg-rust-700 hover:bg-rust-800 text-white font-sans text-sm font-semibold px-8 py-4 rounded-sm cursor-pointer transition-colors"
            >
              Begin Your Project
            </Link>
            <a
              href={PHONE_HREF}
              className="whitespace-nowrap inline-flex items-center gap-2 border border-white/35 text-white font-sans text-sm font-semibold px-8 py-4 rounded-sm hover:bg-white/10 cursor-pointer transition-colors"
            >
              <i className="ri-phone-fill"></i>
              {PHONE}
            </a>
          </div>
        </div>

        {/* Badge */}
        <div className="absolute top-28 right-4 md:right-12 lg:right-16 hidden md:block">
          <div className="w-28 h-28 lg:w-32 lg:h-32">
            <img src={BADGE_URL} alt="30 years badge" className="w-full h-full object-contain drop-shadow-lg" />
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services Overview – Company Story + Gallery */}
      <section className="bg-white py-20 md:py-28">
        <div className="w-full px-4 md:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            {/* Story content */}
            <div>
              <span className="font-sans text-xs font-semibold tracking-[0.22em] uppercase text-rust-600 mb-4 block">
                Our Heritage
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-charcoal leading-tight mb-6">
                Three Decades of Transforming London Properties
              </h2>
              <p className="font-sans text-base text-stone-500 leading-relaxed mb-5">
                Upper Canada Landscape was built on a foundation of artisan craftsmanship and genuine care
                for the communities we serve. For over {YEARS_EXPERIENCE} years, we have been designing and
                building outdoor spaces that families enjoy for generations.
              </p>
              <p className="font-sans text-base text-stone-500 leading-relaxed mb-8">
                From our first stone patio to our most complex multi-feature backyard transformation,
                every project reflects the same commitment to quality that has made us London&rsquo;s most
                trusted landscaping partner.
              </p>

              {/* Pull quote */}
              <div className="border-l-4 border-rust-500 pl-5 py-2 my-8">
                <p className="font-serif text-xl italic text-charcoal leading-relaxed">
                  &ldquo;We don&rsquo;t just build landscapes. We build the spaces families gather in for generations.&rdquo;
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-10">
                {[
                  { v: `${YEARS_EXPERIENCE}+`, l: "Years" },
                  { v: "4", l: "Core Services" },
                  { v: "6+", l: "Regions" },
                ].map((s) => (
                  <div key={s.l} className="text-center bg-cream-100 rounded-xl p-5">
                    <p className="font-serif text-3xl font-bold text-rust-700">{s.v}</p>
                    <p className="font-sans text-xs text-stone-500 mt-0.5">{s.l}</p>
                  </div>
                ))}
              </div>

              {/* Value pillars */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-medal-fill", label: "Artisan Craftsmanship" },
                  { icon: "ri-handshake-fill", label: "Client Partnership" },
                  { icon: "ri-leaf-fill", label: "Environmental Care" },
                  { icon: "ri-shield-check-fill", label: "Full Transparency" },
                ].map((v) => (
                  <div key={v.label} className="flex items-center gap-3 bg-stone-50 border border-stone-100 rounded-lg p-3.5">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-rust-700/10 flex-shrink-0">
                      <i className={`${v.icon} text-rust-600 text-xs`}></i>
                    </div>
                    <span className="font-sans text-xs font-semibold text-charcoal">{v.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Gallery grid */}
            <div className="grid grid-cols-2 gap-3">
              {GALLERY_IMAGES.slice(0, 4).map((img, i) => (
                <div
                  key={i}
                  className={`overflow-hidden rounded-xl ${i === 0 ? "col-span-2 h-56" : "h-44"}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. AI Answer Block */}
      <AIAnswerBlock heading={aboutAIContent.heading} paragraphs={aboutAIContent.paragraphs} />

      {/* 5. Process */}
      <ProcessSection />

      {/* 6. Why Choose Us */}
      <WhyChooseUs />

      {/* 7. Service Areas */}
      <ServiceAreasSection />

      {/* 8. Testimonials */}
      <Testimonials />

      {/* 9. FAQ */}
      <FAQSection heading="About Upper Canada Landscape" items={aboutFAQs} />

      {/* 10. Final CTA */}
      <FinalCTA
        heading="Experience the Upper Canada Landscape Difference"
        subtext="Request your free consultation and discover what sets our craftsmanship apart from the rest."
        bgImage="https://uppercanadalandscape.com/wp-content/uploads/2026/03/Natural-Stone-Pond-and-Backyard-Waterfall-Feature.webp"
      />
    </main>
  );
}
